import { FieldTypeEnum } from "@/core/enums/field-types";
import { createSysComment } from "@/templates/ativacaoped/config/comments.config";
import { isNil } from "lodash";
import {
  isNotWorkflowReview,
  WorkflowReviewEnum
} from "../../businessplan.types";
import {
  adjustmentStep,
  adjustmentAccountingStep,
  getCurrentStep,
  isPairedWithBackend,
  setParityWithBackend
} from "../../../config/workflow.config";
import { TiposResponsaveisEnum } from "../../../responsaveis/responsaveis.types";

/**
 * @typedef WorkflowFormData
 * @param {boolean} opened estado da modal (aberta ou fechada)
 * @param {Object} data data para o formulário
 * @param {string} data.comment comentário obrigatório do revisor
 * @param {string} title título da modal
 * @param {import("../../businessplan.types").WorkflowReviewType} type
 */

/**
 * @typedef RevisorsFormData
 * @param {boolean} opened estado da modal (aberta ou fechada)
 * @param {Object} data data para o formulário
 * @param {number | number[]} data.approvers id(s) do(s) aprovador(es) da próxima etapa
 */

/**
 * // TODO esse typedef vai ser a interface do table-v ou types do Teros
 * @typedef TableColumn
 * @param {string} key identificador da coluna/campo
 * @param {string} name label que será exibido para o usuário
 * @param {string} type tipo de coluna/campo segundo o enum `FieldTypeEnum`
 * @param {Array<{ rule: string; params: any }>} [rules] data para o formulário
 * @param {boolean} [hideInForm] determina que pode aparecer num table-v, mas não numa modal de edição do master-detail
 */

/**
 * @typedef {Object} Comment
 * @property {string} comentario - O texto do comentário
 * @property {number} [revisor] - ID do revisor (opcional)
 * @property {boolean|null} [status=null] - Status do comentário (opcional, valor padrão null)
 */

/**
 * @mixin
 * @description Esse mixins pelas ações de workflow do BP, que aparecem no rodapé da página.
 * @requires {@link BusinessPlanCore} requer o mixin base para o `businessPlanId`, `clientId` e `formData`.
 * @example
 * mixins: [BusinessPlanWorkflow]
 */
export default {
  data() {
    return {
      reviewModal: {
        data: {
          comment: ""
        },
        opened: false
      },
      /** @type {import('../../businessplan.types').Responsavel[]} */
      workflowApprovers: [],
      /** @type {WorkflowFormData} */
      workflowModal: {
        data: {
          comment: ""
        },
        opened: false,
        title: "Justificativa",
        type: WorkflowReviewEnum.CHANGE
      },
      /** @type {RevisorsFormData} */
      workflowRevisorsModal: {
        data: {
          approvers: []
        },
        opened: false
      }
    };
  },
  computed: {
    /**
     * Recupera os dados locais do workflow atual segundo o backend
     * @type {import('./businessplan.types').FrontendWorkflowStep | undefined}
     * @readonly
     */
    currentWorkflow() {
      return getCurrentStep(this.formData?.workflow?.nome);
    },
    /**
     * Determina se o usuário logado foi selecionado como revisor deste BP
     * @type {boolean}
     * @readonly
     */
    isReviewer() {
      const wasSelected = this.reviewers.some(
        ({ responsavel }) => this.user?.responsavel?.id === responsavel?.id
      );
      return !this.isDemandant && wasSelected;
    },
    /**
     * Determina se o BP está numa etapa de ajuste. Para diferenciar de um BP que não saiu da fase de Em Preenchimento.
     * @type {boolean}
     * @readonly
     */
    inWorkflowReview() {
      const { id, workflow_id } = this.formData || {};
      return (
        !isNil(id) &&
        !isNil(workflow_id) &&
        [adjustmentStep.id, adjustmentAccountingStep.id].includes(workflow_id)
      );
    },
    /**
     * Determina se o usuário logado é um responsável com o nível hierarquico FP&A
     * Testando o label para funcionar para o cliente XP Inc. Não pensado para outros clientes.
     * @type {boolean}
     * @readonly
     */
    hasAdminRole() {
      return this.user.nivel_hierarquico?.tipo_id === TiposResponsaveisEnum.ADMINISTRADOR;
    },
    /**
     * Campos para envio dos ajustes solicitados do BP, aceitos pelo componente FormModal
     * @type {TableColumn[]}
     * @readonly
     */
    reviewFields() {
      return [
        {
          key: "subtitle",
          name: `Adicione um comentário para FP&A dos ajustes realizados.`,
          type: FieldTypeEnum.SUBTITLE
        },
        {
          key: "comment",
          name: "Comentário",
          type: FieldTypeEnum.TEXTAREA,
          rules: [
            { rule: "required" },
            { rule: "max", params: { size: 6000 } },
            { rule: "min", params: { size: 3 } }
          ]
        }
      ];
    },
    /**
     * Informa os aprovadores da etapa atual do workflow
     * @type {Array}
     * @readonly
     */
    reviewers() {
      const responsible = this.formData?.responsaveis ?? [];
      return responsible.filter(
        ({ workflow_id }) => workflow_id === this.currentWorkflow?.id
      );
    },
    /**
     * Exibe ou não o botão para se tornar um dos revisores da etapa que o BP está no momento.
     * Caso o usuário não seja demandante, tenha nível hierarquico FP&A, já não tenha o número mínimo de aprovadores selecionados e o usuário atual já não é um revisor deste BP.
     * @type {boolean}
     * @readonly
     */
    showTurnMeReviewerBtn() {
      if (!this.currentWorkflow) {
        return false;
      }

      const { requiredHierarchicalLevels, selfReviewer } = this.currentWorkflow;
      const hasRequiredHierarchicalLevel = requiredHierarchicalLevels.includes(
        this.user.responsavel?.nivel_hierarquico_id
      );

      return (
        !this.isDemandant &&
        !this.isReviewer &&
        selfReviewer &&
        hasRequiredHierarchicalLevel
      );
    },
    /**
     * Caso a etapa esteja aprovada, mas não foi selecionado os revisores, exibe o botão para escolher os revisores da próxima etapa.
     * @type {boolean}
     * @readonly
     */
    showSelectReviewBtn() {
      if (!this.currentWorkflow?.nextStep) {
        return false;
      }

      const { maxReviewers, minReviewers, nextStep } = this.currentWorkflow;

      const {
        id: nextId,
        defineReviewers: nextDefineReviewers,
        minReviewers: nextMinReviewers
      } = nextStep;

      const currentApprovedReviewers = this.reviewers.filter(
        ({ status }) => status === 1
      );

      const responsible = this.formData?.responsaveis ?? [];
      const selectedNextReviwers = responsible.filter(
        ({ workflow_id }) => workflow_id === nextId
      );

      // Se usuário for revisor do workflow atual, o workflow atual já ter todas as aprovações,
      // o próximo workflow precisar de revisores e ainda não ter o mínimo de revisores selecionados,
      // mostrar o botão para selecionar revisores
      return (
        this.isReviewer &&
        currentApprovedReviewers.length >= minReviewers &&
        currentApprovedReviewers.length <= maxReviewers &&
        nextDefineReviewers &&
        nextMinReviewers > 0 &&
        selectedNextReviwers.length < nextMinReviewers
      );
    },
    /**
     * Caso a etapa esteja aprovada, mas não foi selecionado os revisores, exibe o botão para escolher os revisores da próxima etapa.
     * @type {boolean}
     * @readonly
     */
    showChangeWorkflowBtn() {
      if (!this.currentWorkflow?.nextStep) {
        return false;
      }

      const { maxReviewers, minReviewers, nextStep } = this.currentWorkflow;

      const {
        id: nextId,
        defineReviewers: nextDefineReviewers,
        maxReviewers: nextMaxReviewers,
        minReviewers: nextMinReviewers
      } = nextStep;

      const currentApprovedReviewers = this.reviewers.filter(
        ({ status }) => status === 1
      );

      const responsible = this.formData?.responsaveis ?? [];
      const selectedNextReviwers = responsible.filter(
        ({ workflow_id }) => workflow_id === nextId
      );

      // Se usuário for revisor do workflow atual, o workflow atual já ter todas as aprovações,
      // o próximo workflow precisar de revisores e ainda ter o mínimo de revisores selecionados,
      // mostrar o botão para mudar para a próxima etapa
      return (
        this.isReviewer &&
        currentApprovedReviewers.length >= minReviewers &&
        currentApprovedReviewers.length <= maxReviewers &&
        nextDefineReviewers &&
        nextMinReviewers > 0 &&
        selectedNextReviwers.length >= nextMinReviewers &&
        selectedNextReviwers.length <= nextMaxReviewers
      );
    },
    /**
     * Caso o BP esteja em aprovação e o usuário logado for um revisor e ainda não tiver realizado a revisão, exibe os botões de ações
     * @type {boolean}
     * @readonly
     */
    showWorkflowActionBtns() {
      return (
        this.formData?.workflow &&
        !this.currentWorkflow?.selfReviewer &&
        this.isReviewer &&
        this.reviewers.some(
          ({ responsavel, status }) =>
            this.user?.responsavel?.id === responsavel?.id && status !== 1
        )
      );
    },
    /**
     * Campos para seleção dos revisores da próxima etapa do workflow, aceitos pelo componente FormModal
     * @type {TableColumn[]}
     * @readonly
     */
    workflowReviewFields() {
      const max = this.currentWorkflow?.nextStep?.maxReviewers ?? 1;
      const min = this.currentWorkflow?.nextStep?.minReviewers ?? 1;
      const subtitle =
        max > 1
          ? `Selecione os revisores para a próxima etapa ${this.currentWorkflow?.nextStep?.name}.`
          : `Selecione o revisor para a próxima etapa ${this.currentWorkflow?.nextStep?.name}.`;
      const field = max > 1 ? "Aprovadores" : "Aprovador";

      return [
        {
          key: "subtitle",
          name: subtitle,
          type: FieldTypeEnum.SUBTITLE
        },
        {
          key: "tooltip",
          name: "Favor incluir um N3 para aprovação após CFO, recomendamos que seja o CTO.",
          type: FieldTypeEnum.SUBTITLE,
          hideInform: !this.currentWorkflow?.nextStep?.isEquivalent("Aprovado CFO - Em análise Executiva")
        },
        {
          key: "approvers",
          name: field,
          type:
            max > 1
              ? FieldTypeEnum.AUTOCOMPLETE_MULTIPLE
              : FieldTypeEnum.AUTOCOMPLETE,
          rel: { to: this.workflowApprovers, key: "id", name: "label" },
          rules:
            max > 1
              ? [
                { rule: "min", params: { size: min } },
                { rule: "max", params: { size: max } }
              ]
              : [{ rule: "required" }]
        }
      ];
    },
    /**
     * Campos para aprovação/reprovação/solicitar alteração do BP, aceitos pelo componente FormModal
     * @type {TableColumn[]}
     * @readonly
     */
    workflowFields() {
      const action =
        this.workflowModal.type === WorkflowReviewEnum.APPROVE
          ? "aprovar"
          : this.workflowModal.type === WorkflowReviewEnum.REPROVE
            ? "reprovar"
            : "solicitar ajustes";

      return [
        {
          key: "subtitle",
          name: `Adicione uma justificativa para ${action} desta etapa.`,
          type: FieldTypeEnum.SUBTITLE
        },
        {
          key: "comment",
          name: "Justificativa",
          type: FieldTypeEnum.TEXTAREA,
          rules: [
            { rule: "required" },
            { rule: "max", params: { size: 6000 } },
            { rule: "min", params: { size: 3 } }
          ]
        }
      ];
    }
  },
  methods: {
    /**
     * Carrega a lista revisores e abre a modal de seleção de revisores da próxima etapa.
     * O disparo dessa modal (outro método) é quem passa o BP para a próxima etapa.
     */
    async openSelectReviewersModal() {
      if (!this.currentWorkflow?.nextStep) {
        return;
      }

      try {
        await this.workflowGetReviewers();
      } catch {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro ao recuperar a lista de responsáveis",
          text:
            "Tente definir os aprovadores da próxima etapa novamente e caso o erro persista contacte o suporte."
        });
      }

      const responsibles = this.formData?.responsaveis ?? [];
      let nextStepReviewers = responsibles.filter(
        ({ workflow_id }) => workflow_id === this.currentWorkflow.nextStep?.id
      );

      if (!Array.isArray(nextStepReviewers)) {
        nextStepReviewers = [];
      }

      // Fluxo agora é concluído no saveNextWorkflowReviewers
      this.workflowRevisorsModal.data.approvers =
        this.currentWorkflow?.nextStep?.maxReviewers > 1 ? nextStepReviewers : null;
      this.workflowRevisorsModal.opened = true;
    },
    /**
     * Redireciona o usuário através do VueRouter para listagem de Workflow > Acompanhamento
     */
    goToWorkflow() {
      this.$router.push({
        name: "ativacaoped-workflow-acompanhamento"
      });
    },
    /**
     * Recupera a lista de etapad da API e atribui os dados necessários para a lista local de etapas
     */
    async initializeLocalWorkflow() {
      if (isPairedWithBackend()) {
        return;
      }

      try {
        const resourceWorkflows = this.apiResource(
          `v1/ativacaoped/${this.clientId}/workflow-parametrizacao`
        );

        const response = await resourceWorkflows.get();

        if (response.data) {
          setParityWithBackend(response.data);
        }
      } catch {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro ao recuperar a lista de etapas do workflow",
          text: "Por favor, atualize a página."
        });
      }
    },
    /**
     * Abre uma modal de confirmação antes de atualizar a lista de revisores do workflow atual para incluir o usuário logado.
     * Após isso recarrega todos os dados do BP.
     */
    async turnMeReviewer() {
      try {
        if (!this.user?.responsavel?.id) {
          this.$notify({
            group: "geral",
            duration: 5000,
            type: "error",
            title: "Erro ao definir revisor da etapa",
            text: "Você não possuí um vínculo de responsável para esta empresa."
          });

          return;
        }

        if (!this.currentWorkflow) {
          this.$notify({
            group: "geral",
            duration: 5000,
            type: "error",
            title: "Erro ao definir revisor da etapa",
            text: "Não foi possível recuperar a etapa atual do workflow."
          });

          return;
        }

        const confirm = await this.$refs.turnMeRevisorConfirmModal.asyncOpen();

        if (confirm) {
          // Isso garante que todas as etapas que devem possuir revisores em comum saibam quem é esse revisor
          const { id, shareReviewersWith } = this.currentWorkflow ?? {};
          const nextWorkflowIds = shareReviewersWith?.map(({ id }) => id) ?? [];
          nextWorkflowIds.push(id);

          // Se define como revisor em todas as etapas necessárias
          const resourceChangeWorkflow = this.apiResource(
            `v1/ativacaoped/${this.clientId}/business-plans-workflow/${this.businessPlanId}`
          );
          const requests = nextWorkflowIds.map(workflow_id =>
            resourceChangeWorkflow.save({
              revisores: [this.user.responsavel.id],
              workflow_id
            })
          );
          await Promise.all(requests);

          // Envia para a próxima etapa
          await this.saveSysComment("se tornou revisor");
          await this.sendToNextWorkflow("approve");
          this.doLoad();
        }
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    /**
     * Valida se todos os campos obrigatórios foram preenchidos corretamente.
     * Caso sim, solicita um comentário do demandante.
     */
    async requestedChangesValidation() {
      if (!this.validateForms(WorkflowReviewEnum.APPROVE)) {
        this.$refs.approveErrorModal?.open();
        return;
      }

      await this.doSave({ skip: true });
      this.reviewModal.opened = true;
    },
    /**
     * Salva o comentário do usuário no backend para a etapa atual do sistema
     * @param {string} comment - O texto do comentário
     * @param {boolean|null} [status=null] - Status do comentário (opcional, valor padrão null)
     */
    async saveComment(comment, status = null) {
      try {
        const approveUrl = `/v1/ativacaoped/${this.clientId}/business-plans-workflow/${this.businessPlanId}/aprovacao-revisor`;
        const approveResource = this.apiResource(approveUrl);
        const response = await approveResource.save({
          comentario: comment,
          revisor: this.user.id,
          status
        });
        return response;
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
        return;
      }
    },
    /**
     * Salva o comentário do sistema para histórico de ações do workflow
     * @param {string} comment - O texto do comentário
     */
    async saveSysComment(comment) {
      try {
        const url = `/v1/ativacaoped/${this.clientId}/business-plans-workflow/${this.businessPlanId}/aprovacao-revisor`;
        await this.api().post(url, {
          comentario: createSysComment(comment),
          revisor: this.user.id,
          status: null
        });
      } catch (error) {
        console.error("Não foi possível realizar comentário do sistema.", {
          error
        });
        return;
      }
    },
    /**
     * Define o(s) aprovador(es) para a próxima etapa do workflow, se necessário
     * @param {Object} data dados dos campos do FormModal
     * @param {Function} close função para fechar o FormModal programaticamente
     */
    async saveNextWorkflowReviewers(data, close) {
      try {
        const { approvers } = data || {};

        if (
          !approvers ||
          !(
            typeof approvers === "number" ||
            (Array.isArray(approvers) && approvers.length)
          )
        ) {
          this.$notify({
            group: "geral",
            duration: 5000,
            type: "error",
            title: "Erro ao definir revisores da próxima etapa",
            text: "Quantidade de revisores insuficiente."
          });
          return;
        }

        await this.sendToNextWorkflow(
          WorkflowReviewEnum.APPROVE,
          Array.isArray(approvers) ? approvers : [approvers]
        );

        const suffix = Array.isArray(approvers)
          ? "Os revisores da próxima etapa foram selecionados."
          : "O revisor da próxima etapa foi selecionado.";
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "sucess",
          title: "Aprovação concluída",
          text: `Número mínimo de aprovações atingidas, o Business Plan foi passado para a próxima etapa. ${suffix}`
        });

        if (close) {
          close();
        }

        await this.saveSysComment("definiu os revisores da próxima etapa");
        this.goBack();
      } catch {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "alert",
          title: "Aprovação concluída com problemas",
          text:
            "O Business Plan foi para a próxima etapa do workflow, mas nenhum aprovador foi selecionado. Tente novamente e caso o erro persista, contate o suporte."
        });
      }
    },
    /**
     * Valida se todos os campos obrigatórios foram preenchidos corretamente.
     * Caso sim, envia o BP para a primeira etapa do Workflow ou de volta para o começo do Workflow caso esteja na etapa de Ajustes ou de volta para Análise Contábil se em etapa de Ajuste Contábil.
     */
    async sendToApproval() {
      try {
        if (!this.validateForms(WorkflowReviewEnum.APPROVE)) {
          this.$refs.approveErrorModal?.open();
          return;
        }

        await this.doSave({ skip: true });
        await this.sendToNextWorkflow(WorkflowReviewEnum.APPROVE);
        await this.saveSysComment("enviou para aprovação");

        this.$notify({
          group: "geral",
          duration: 5000,
          type: "sucess",
          title: "Business Plan enviado para análise",
          text:
            "Você pode acompanhar o progresso na tela Acompanhamento, dentro do menu Worklow"
        });
        this.goToWorkflow();
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    /**
     * Após o preenchimento do comentário do demandante, envia o BP para a etapa de Análise de FP&A do Workflow.
     * @param {Object} data dados dos campos do FormModal
     * @param {Function} close função para fechar o FormModal programaticamente
     */
    async sendRequestedChangesToApproval(data, close) {
      if (!data?.comment || data.comment.length < 3) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Comentário inválido",
          text:
            "Para concluir a ação você precisa informar um comentário com no mínimo 3 caracteres."
        });

        return;
      }

      try {
        await this.saveComment(data.comment);
      } catch {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro ao salvar comentário",
          text: "Tente novamente e caso o erro persista contacte o suporte."
        });

        return;
      }

      try {
        await this.sendToNextWorkflow(WorkflowReviewEnum.APPROVE);
      } catch {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro ao enviar ajustes",
          text:
            "Seus ajustes e comentário foram salvos, mas não enviados para FP&A re analisar. Tente novamente ou contacte o suporte."
        });
      }

      this.$notify({
        group: "geral",
        duration: 5000,
        type: "sucess",
        title: "Ajustes enviados com sucesso",
        text: "Business Plan será analisado pelo FP&A."
      });

      await this.saveSysComment("enviou novamente para aprovação");

      if (close) {
        close();
      }
      this.goBack();
    },
    /**
     * Altera a etapa de workflow do BP para uma das etapas de ajuste, para reprovação ou a próxima etapa do fluxo.
     * Deve ser usada logo após salvar o comentário justificando a ação.
     * @param {import("../../businessplan.types").WorkflowReviewType} [mode='approve']
     * @param {Array<number>} [revisors]
     */
    async sendToNextWorkflow(mode = WorkflowReviewEnum.APPROVE, revisors) {
      if (!this.currentWorkflow && mode !== WorkflowReviewEnum.APPROVE) {
        // TODO error
        throw new Error(
          "Não foi possível recuperar a etapa atual do workflow."
        );
      }

      let nextWorkflowId = undefined;

      const { adjustmentStep, nextStep, reprovalStep } = this
        .currentWorkflow ?? { nextStep: { id: undefined } };

      switch (mode) {
        case WorkflowReviewEnum.APPROVE:
          nextWorkflowId = nextStep.id;
          break;
        case WorkflowReviewEnum.REPROVE:
          nextWorkflowId = reprovalStep.id;
          break;
        case WorkflowReviewEnum.CHANGE:
        default:
          nextWorkflowId = adjustmentStep.id;
      }

      // Define os revisores e altera a etapa
      const resourceChangeWorkflow = this.apiResource(
        `v1/ativacaoped/${this.clientId}/business-plans-workflow/${this.businessPlanId}`
      );
      await resourceChangeWorkflow.save({
        revisores: revisors || undefined,
        workflow_id: nextWorkflowId
      });
    },
    /**
     * Recupera todos os responsáveis da empresa, ordenando por Nível Hierárquico sugerido pra a etapa do workflow atual e outros, depois por nome do responsável.
     */
    async workflowGetReviewers() {
      try {
        const resource = this.apiResource(
          `v1/ativacaoped/${this.clientId}/responsaveis/selecao?workflow_id=${this.currentWorkflow.nextStep.id}&business_plan_id=${this.businessPlanId}`
        );
        const response = await resource.get();
        // TODO preciso ordenar por nível hierarquico e usar subgrupos para mostrar o nível atual e os outros
        this.workflowApprovers = response
          .sort(({ nome: a }, { nome: b }) => a.localeCompare(b))
          .map(({ email, id, nome }) => ({ id, label: `${nome} (${email})` }));
      } catch (error) {
        this.workflowApprovers = [];

        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    /**
     * Configura e abre a modal para inserção da justificativa da aprovação/reprovação/ajuste do Workflow.
     * @param {import("../../businessplan.types").WorkflowReviewType} modal determina qual fluxo de revisão será executado
     */
    async workflowOpenModal(modal) {
      this.workflowModal.type = modal;
      this.workflowModal.data = {
        comment: ""
      };
      this.workflowModal.title =
        modal === WorkflowReviewEnum.APPROVE
          ? "Aprovar etapa"
          : modal === WorkflowReviewEnum.REPROVE
            ? "Reprovar etapa"
            : "Solicitar ajustes";
      this.workflowModal.opened = true;
    },
    /**
     * Valida a revisão e salva através da API o resultado mais comentário da revisão.
     * Caso o usuário seja o ultimo revisor a aprovar o BP, abre a modal para seleção dos revisores da próxima etapa.
     * @param {Object} data dados dos campos do FormModal
     * @param {Function} close função para fechar o FormModal programaticamente
     */
    async workflowSendReview(data, close) {
      if (!this.user?.responsavel?.id) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Você não é um responsável válido",
          text:
            "Não foi encontrado nenhum registro de responsável vinculado ao seu usuário."
        });

        return;
      }

      if (!data?.comment || data.comment.length < 3) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Justificativa inválida",
          text:
            "Para concluir a ação você precisa informar uma justificativa com no mínimo 3 caracteres."
        });

        return;
      }

      const throwInternalError = () => {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Ação não identificada",
          text: "Erro interno. Por favor, contactar o suporte."
        });
      };

      if (
        !this.workflowModal?.type ||
        isNotWorkflowReview(this.workflowModal.type)
      ) {
        throwInternalError();
        return;
      }

      if (this.workflowModal.type === WorkflowReviewEnum.CHANGE) {
        try {
          await this.saveComment(data.comment);
          await this.sendToNextWorkflow(WorkflowReviewEnum.CHANGE);
          await this.saveSysComment("solicitou ajustes ao demandante");
          this.$notify({
            group: "geral",
            duration: 5000,
            type: "sucess",
            title: "Requisição de ajuste enviada.",
            text:
              "O Business Plan foi para a etapa de ajuste. O demandante será notificado."
          });

          if (close) {
            close();
          }

          this.goBack();
        } catch (error) {
          this.$notify({
            group: "geral",
            duration: 5000,
            type: "error",
            title: "Erro ao enviar o Business Plan para etapa de ajuste",
            text:
              "Tente novamente mais tarde e caso o erro persista, contacte o suporte."
          });
        }

        return;
      }

      if (this.workflowModal.type === WorkflowReviewEnum.REPROVE) {
        try {
          await this.saveComment(data.comment, false);
          await this.sendToNextWorkflow(WorkflowReviewEnum.REPROVE, [
            this.user.responsavel.id
          ]);
          await this.saveSysComment("reprovou o Business Plan");
          this.$notify({
            group: "geral",
            duration: 5000,
            type: "sucess",
            title: "Reprovação concluída com sucesso",
            text:
              "O Business Plan foi reprovado e saiu do fluxo de aprovação. O demandante será notificado."
          });

          if (close) {
            close();
          }

          this.goBack();
        } catch (error) {
          this.$notify({
            group: "geral",
            duration: 5000,
            type: "error",
            title: "Erro ao reprovar o Business Plan",
            text:
              "Tente novamente mais tarde e caso o erro persista, contacte o suporte."
          });
        }

        return;
      }

      if (this.workflowModal.type === WorkflowReviewEnum.APPROVE) {
        try {
          await this.saveComment(data.comment, true);
          await this.saveSysComment("aprovou o Business Plan");
        } catch {
          this.$notify({
            group: "geral",
            duration: 5000,
            type: "error",
            title: "Erro durante a aprovação",
            text: "Tente novamente e caso o erro persista contacte o suporte."
          });
          return;
        }

        const doLoad = this.doLoad;
        // Pega os dados mais atualizados, como a lista de responsaveis que já aprovou o projeto
        const loadData = async (tries = 3) => {
          try {
            await doLoad();
            return true;
          } catch {
            if (tries > 0) {
              const sucess = await loadData(tries - 1);
              return sucess;
            } else {
              this.$notify({
                group: "geral",
                duration: 5000,
                type: "error",
                title: "Erro durante recuperação dos dados",
                text:
                  "Atualize a página e tente novamente. Caso o erro persista contacte o suporte."
              });
              return false;
            }
          }
        };

        const isDataUpdated = await loadData();

        if (!isDataUpdated) {
          if (close) {
            close();
          }

          return;
        }

        if (!this.currentWorkflow) {
          throwInternalError();
          return;
        }

        const { nextStep } = this.currentWorkflow;
        const someReviewPending = this.reviewers.some(({ status }) => status !== 1);

        if (someReviewPending) {
          this.$notify({
            group: "geral",
            duration: 5000,
            type: "sucess",
            title: "Aprovação concluída com sucesso",
            text:
              "Aguardando a revisão dos outros revisores antes de alterar a etapa atual."
          });

          if (close) {
            close();
          }

          this.goBack();
          return;
        }

        if (!nextStep.defineReviewers) {
          try {
            await this.sendToNextWorkflow(WorkflowReviewEnum.APPROVE);
            this.$notify({
              group: "geral",
              duration: 5000,
              type: "sucess",
              title: "Aprovação concluída com sucesso",
              text: "Business Plan enviado para a próxima etapa."
            });
          } catch {
            this.$notify({
              group: "geral",
              duration: 5000,
              type: "error",
              title: "Aprovação não concluída",
              text:
                "Sua justificativa foi salva, mas ocorreu um erro ao enviar o Business Plan enviado para a próxima etapa. Tente novamente mais tarde."
            });
          } finally {
            if (close) {
              close();
            }

            this.goBack();
          }

          return;
        }

        const responsibles = this.formData?.responsaveis ?? [];
        const nextStepReviewers = responsibles.filter(
          ({ workflow_id }) => workflow_id === nextStep?.id
        );

        if (
          nextStep.defineReviewers &&
          nextStepReviewers.length < nextStep.maxReviewers
        ) {
          this.openSelectReviewersModal();

          if (close) {
            close();
          }

          return;
        }

        try {
          await this.sendToNextWorkflow(WorkflowReviewEnum.APPROVE, nextStepReviewers.map(({ responsavel_id }) => responsavel_id));
          this.$notify({
            group: "geral",
            duration: 5000,
            type: "sucess",
            title: "Aprovação concluída",
            text: `Número mínimo de aprovações atingidas, o Business Plan foi passado para a próxima etapa.`
          });
        } catch {
          this.$notify({
            group: "geral",
            duration: 5000,
            type: "error",
            title: "Aprovação não concluída",
            text:
              "Sua justificativa foi salva, mas ocorreu um erro ao enviar o Business Plan enviado para a próxima etapa. Tente novamente mais tarde."
          });
        } finally {
          if (close) {
            close();
          }

          this.goBack();
        }

        return;
      }

      throwInternalError();
    }
  }
};
